/**
 * Shared layout component for consent pages
 * 
 * This component provides a consistent layout structure for both
 * AuthPage and GuestPage, reducing code duplication and ensuring
 * a uniform user experience across different consent flows.
 */

import React from 'react';
import { PageContainer } from '@amzn/stencil-react-components/page';
import { View, Spacer } from '@amzn/stencil-react-components/layout';
import { Text, H1, H2, H3 } from '@amzn/stencil-react-components/text';
import { Card } from '@amzn/stencil-react-components/card';
import { MessageBanner } from '@amzn/stencil-react-components/message-banner';
import { MainWithSkipLink } from '@amzn/stencil-react-components/a11y';
import { ConsentHeader } from './ConsentHeader';
import { ConsentFooter } from './ConsentFooter';
import { ConsentForm } from './ConsentForm';
import { LoadingState } from '../../config/consentConfig';
import { UserInfo, TokenData } from '../../utils/ConsentApiService';

// Props for the main consent form layout
interface ConsentFormLayoutProps {
  consentDecision: 'accept' | 'decline' | null;
  onConsentDecisionChange: (decision: 'accept' | 'decline') => void;
  showTextInput: boolean;
  consentText?: string;
  onConsentTextChange?: (text: string) => void;
  validationError?: string | null;
  requiredConsentText?: string;
  isAcceptEnabled?: boolean;
  onSubmit: () => Promise<void>;
  isSubmitDisabled: boolean;
  isLoading: boolean;
}

// Props for the loading state
interface LoadingStateProps {
  title: string;
  message: string;
}

// Props for the error state
interface ErrorStateProps {
  title: string;
  message: string;
  showRefreshHint?: boolean;
}

// Props for the success/completion state
interface CompletionStateProps {
  consentDecision: 'accept' | 'decline';
  userInfo?: UserInfo;
  tokenData?: TokenData;
  userType: 'guest' | 'authenticated';
  onSignOut?: () => Promise<void>;
  isSigningOut?: boolean;
}

// Main layout props
interface ConsentPageLayoutProps {
  children: React.ReactNode;
}

// Base layout component
export const ConsentPageLayout: React.FC<ConsentPageLayoutProps> = ({ children }) => (
  <MainWithSkipLink>
    <View style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ConsentHeader />
      <PageContainer paddingTop="0rem" style={{ flex: 1 }}>
        {children}
      </PageContainer>
    </View>
  </MainWithSkipLink>
);

// Loading state component
export const ConsentLoadingState: React.FC<LoadingStateProps> = ({ title, message }) => (
  <MainWithSkipLink>
    <PageContainer paddingTop="0rem">
      <ConsentHeader />
      <Spacer height="L400" />
      <MessageBanner>
        <div role="status" aria-live="polite" aria-label={`Loading: ${title}`}>
          <H2 id="loading-title">
            <span aria-hidden="true">🔄</span> {title}
          </H2>
          <Text aria-describedby="loading-title">{message}</Text>
        </div>
      </MessageBanner>
    </PageContainer>
  </MainWithSkipLink>
);

// Error state component
export const ConsentErrorState: React.FC<ErrorStateProps> = ({ 
  title, 
  message, 
  showRefreshHint = true 
}) => (
  <MainWithSkipLink>
    <PageContainer paddingTop="0rem">
      <ConsentHeader />
      <Spacer height="L400" />
      <MessageBanner>
        <div role="alert" aria-live="assertive" aria-label={`Error: ${title}`}>
          <H2 id="error-title">
            <span aria-hidden="true">❌</span> {title}
          </H2>
          <Text aria-describedby="error-title">{message}</Text>
          {showRefreshHint && (
            <>
              <Spacer height="M200" />
              <Text>Please try refreshing the page or contact support if the issue persists.</Text>
            </>
          )}
        </div>
      </MessageBanner>
    </PageContainer>
  </MainWithSkipLink>
);

// Main consent form layout
export const ConsentFormLayout: React.FC<ConsentFormLayoutProps> = ({
  consentDecision,
  onConsentDecisionChange,
  showTextInput,
  consentText,
  onConsentTextChange,
  validationError,
  requiredConsentText,
  isAcceptEnabled,
  onSubmit,
  isSubmitDisabled,
  isLoading
}) => (
  <MainWithSkipLink>
    <View style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <ConsentHeader />
      
      <PageContainer paddingTop="0rem" style={{ flex: 1 }}>
        {/* Consent Form */}
        <section aria-labelledby="consent-form-title">
          <ConsentForm
            consentDecision={consentDecision}
            onConsentDecisionChange={onConsentDecisionChange}
            showTextInput={showTextInput}
            consentText={consentText}
            onConsentTextChange={onConsentTextChange}
            validationError={validationError}
            requiredConsentText={requiredConsentText}
            isAcceptEnabled={isAcceptEnabled}
          />
        </section>

        <Spacer height="L400" />
      </PageContainer>

      {/* Footer */}
      <footer role="contentinfo" aria-label="Form submission actions">
        <ConsentFooter
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
          isLoading={isLoading}
        />
      </footer>
    </View>
  </MainWithSkipLink>
);

// Completion/Success state component
export const ConsentCompletionState: React.FC<CompletionStateProps> = ({
  consentDecision,
  userInfo,
  tokenData,
  userType,
  onSignOut,
  isSigningOut = false
}) => (
  <MainWithSkipLink>
    <View style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} className="consent-completion">
      {/* Header */}
      <ConsentHeader />
      
      <PageContainer paddingTop="0rem" style={{ flex: 1 }}>
        {/* Completion Content matching ConsentForm design */}
        <View className="consent-content">
          {/* Main Title matching ConsentForm */}
          <View className="consent-content__header">
            <H1 className="main-title">
              {consentDecision === 'accept' ? 'Consent Confirmed' : 'Consent Declined'}
            </H1>
          </View>

          {/* Content Section matching ConsentForm layout */}
          <View className="consent-content__body">
            {/* Left Content */}
            <View className="consent-content__text">
              <H3 className="consent-text consent-text--section-title">
                {consentDecision === 'accept' ? 'Thank you - your consent has been successfully recorded' : 'Meeting Recording Declined'}
              </H3>
              
              <Text className="consent-text">
                {consentDecision === 'accept' 
                  ? 'You\'re now ready to join the investigation meeting using the calendar invite you received. All information shared during this process will be handled according to Amazon\'s privacy and data protection policies.'
                  : 'Your decision has been recorded. The investigation meeting will not be transcribed. Contact your HR representative with any questions.'
                }
              </Text>
              
              <Text className="consent-text consent-text--bold">
                Submitted: {new Date().toLocaleDateString('en-US', { 
                    year: '2-digit', 
                    month: 'numeric', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
              </Text>

              {consentDecision === 'accept' ? (
                <>
                  <Text className="consent-text consent-text--bold">
                    Next Steps:
                  </Text>
                  <Text className="consent-text">
                    Before joining, please make sure you have access to the required meeting tools and find a quiet space for our discussion. 
                    If you need to modify your consent at any point, you can do so within 24 hours using this link, by requesting a new link 
                    from your HR representative, or by informing either your HR representative or the investigation team during the meeting itself.
                  </Text>
                </>
              ) : (
                <>
                  <Text className="consent-text consent-text--bold">
                    To change your decision:
                  </Text>
                  <View className="consent-list">
                    <Text className="list-item">
                      • Resubmit within 24 hours using this link
                    </Text>
                    <Text className="list-item">
                      • Request a new link from HR
                    </Text>
                    <Text className="list-item">
                      • Inform HR during the meeting
                    </Text>
                  </View>
                </>
              )}
              
              {/* Sign out button for authenticated users */}
              {userType === 'authenticated' && onSignOut && (
                <View style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <button 
                    onClick={onSignOut}
                    disabled={isSigningOut}
                    aria-label="Sign out of the application"
                    style={{ 
                      background: 'none', 
                      border: '1px solid var(--color-primary-blue)', 
                      color: 'var(--color-primary-blue)', 
                      textDecoration: 'none',
                      cursor: isSigningOut ? 'not-allowed' : 'pointer',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      fontFamily: '"Amazon Ember"',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      opacity: isSigningOut ? 0.5 : 1
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = '2px solid var(--color-primary-blue)';
                      e.currentTarget.style.outlineOffset = '2px';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.outline = 'none';
                    }}
                  >
                    {isSigningOut ? 'Signing Out...' : 'Sign Out'}
                  </button>
                </View>
              )}
            </View>

            {/* Right Illustration - Status Icon */}
            <View className="consent-content__illustration">
              <View 
                className="illustration-container" 
                role="img" 
                aria-label={`Consent status: ${consentDecision === 'accept' ? 'Accepted' : 'Declined'}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px'
                }}
              >
                <View style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: consentDecision === 'accept' ? '#00A651' : '#D13212',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  <Text 
                    style={{ 
                      fontSize: '36px', 
                      color: 'white',
                      fontWeight: 'bold',
                      lineHeight: '1'
                    }}
                    aria-hidden="true"
                  >
                    {consentDecision === 'accept' ? '✓' : '✕'}
                  </Text>
                </View>
                <Text style={{
                  color: '#232F3E',
                  fontFamily: '"Amazon Ember"',
                  fontSize: '18px',
                  fontWeight: '700',
                  textAlign: 'center'
                }}>
                  {consentDecision === 'accept' ? 'Consent Accepted' : 'Consent Declined'}
                </Text>
                <Text style={{
                  color: '#565959',
                  fontFamily: '"Amazon Ember"',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}>
                  {consentDecision === 'accept' ? 'Ready to proceed' : 'Decision recorded'}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </PageContainer>
    </View>
  </MainWithSkipLink>
);

// Unauthorized access state component with improved UX
export const ConsentUnauthorizedState: React.FC<{ message?: string }> = ({ 
  message = 'Invalid or expired access token.' 
}) => (
  <MainWithSkipLink>
    <View style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ConsentHeader />
      
      <PageContainer paddingTop="0rem" style={{ flex: 1 }}>
        {/* Unauthorized Content matching ConsentForm design */}
        <View className="consent-content">
          {/* Main Title */}
          <View className="consent-content__header">
            <H1 className="main-title">Access Not Authorized</H1>
          </View>

          {/* Content Section */}
          <View className="consent-content__body">
            {/* Left Content */}
            <View className="consent-content__text">
              <H3 className="consent-text consent-text--section-title">
                Unable to Access Consent Form
              </H3>
              
              <Text className="consent-text">
                {message}
              </Text>
              
              <Text className="consent-text consent-text--bold">
                What This Means:
              </Text>
              
              <View className="consent-list">
                <Text className="list-item">
                  • Your consent link may have expired (links are valid for 24 hours)
                </Text>
                <Text className="list-item">
                  • The link may have been used incorrectly or modified
                </Text>
                <Text className="list-item">
                  • There may be a technical issue with the link
                </Text>
              </View>

              <Text className="consent-text consent-text--bold">
                Next Steps:
              </Text>
              
              <Text className="consent-text">
                Please contact your HR representative or the investigation team to request a new consent link. 
                They can generate a fresh link that will be valid for 24 hours from the time it's created.
              </Text>
              
              <Text className="consent-text">
                If you believe this is an error or need immediate assistance, please reach out to your 
                HR representative directly.
              </Text>
            </View>

            {/* Right Illustration - Warning Icon */}
            <View className="consent-content__illustration">
              <View 
                className="illustration-container" 
                role="img" 
                aria-label="Access restricted - authorization required"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px'
                }}
              >
                <View style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#FF9900',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  <Text 
                    style={{ 
                      fontSize: '36px', 
                      color: 'white',
                      fontWeight: 'bold',
                      lineHeight: '1'
                    }}
                    aria-hidden="true"
                  >
                    🔒
                  </Text>
                </View>
                <Text style={{
                  color: '#232F3E',
                  fontFamily: '"Amazon Ember"',
                  fontSize: '18px',
                  fontWeight: '700',
                  textAlign: 'center'
                }}>
                  Access Restricted
                </Text>
                <Text style={{
                  color: '#565959',
                  fontFamily: '"Amazon Ember"',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}>
                  Contact HR for new link
                </Text>
              </View>
            </View>
          </View>
        </View>
      </PageContainer>
    </View>
  </MainWithSkipLink>
);

// Export all components
export default {
  ConsentPageLayout,
  ConsentLoadingState,
  ConsentErrorState,
  ConsentFormLayout,
  ConsentCompletionState,
  ConsentUnauthorizedState
};
